function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

window.ProductSizeGuard = {
  isSizeText(text) {
    const t = (text || '').toLowerCase();
    return t.includes('size') || /(^|[^a-z])top([^a-z]|$)/.test(t) || /(^|[^a-z])bottom([^a-z]|$)/.test(t);
  },

  isSizeInput(input) {
    if (!input) return false;
    const name = (input.name || '').toLowerCase();
    const wrap = input.closest('fieldset, .product-form__input');
    const labelEl = wrap?.querySelector('legend, .form__label, label');
    const label = labelEl?.textContent || '';
    return this.isSizeText(`${name} ${label}`);
  },

  getMissing(root) {
    if (!root) return [];
    const groups = new Map();
    root.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (!this.isSizeInput(input)) return;
      if (!groups.has(input.name)) {
        const wrap = input.closest('fieldset, .product-form__input');
        const labelEl = wrap?.querySelector('legend, .form__label, label');
        const label = (labelEl?.textContent || 'Size').split(':')[0].trim() || 'Size';
        groups.set(input.name, { key: input.name, label, fieldset: wrap, checked: false });
      }
      if (input.checked) groups.get(input.name).checked = true;
    });
    return Array.from(groups.values()).filter((group) => !group.checked);
  },

  getMessage(missing) {
    if (!missing.length) return '';
    const labels = missing.map((group) => group.label || 'Size');
    if (labels.length === 1) return `Please Select ${labels[0]}`;
    return `Please Select ${labels.join(' And ')}`;
  },

  scrollToSize(missing) {
    const target =
      (missing && missing[0] && missing[0].fieldset) ||
      document.querySelector('product-info variant-selects, product-info variant-radios');
    if (!target || typeof target.scrollIntoView !== 'function') return;
    window.setTimeout(function () {
      target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 60);
  },

  uncheckSizeRadios(root) {
    if (!root) return;
    root.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (!this.isSizeInput(input)) return;
      input.checked = false;
      input.removeAttribute('checked');
    });
  },

  /** Uncheck any size that is unavailable for the current colour. Never auto-pick another size. */
  clearUnavailableCheckedSizes(root) {
    if (!root) return false;
    let cleared = false;
    root.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (!this.isSizeInput(input)) return;
      const unavailable =
        input.classList.contains('disabled') ||
        input.classList.contains('visually-disabled') ||
        input.hasAttribute('disabled');
      if (input.checked && unavailable) {
        input.checked = false;
        input.removeAttribute('checked');
        cleared = true;
      }
    });
    return cleared;
  },

  hideShopifyErrors(root) {
    const scope = root || document;
    scope.querySelectorAll('.product-form__error-message-wrapper').forEach((el) => {
      el.setAttribute('hidden', '');
    });
    scope.querySelectorAll('.size-error').forEach((el) => {
      el.style.display = 'none';
    });
  },

  showError(root, missing) {
    if (!root || !missing?.length) return;
    this.hideShopifyErrors(root);

    const message = this.getMessage(missing);
    const picker = root.querySelector('variant-selects, variant-radios');
    let err =
      document.getElementById('custom-size-error-msg') ||
      root.querySelector('[id^="custom-size-error"]');

    if (!err && picker) {
      err = document.createElement('div');
      err.id = 'custom-size-error-msg';
      err.setAttribute('role', 'alert');
      err.style.cssText =
        'color:#d93838;font-size:13px;font-weight:500;display:flex;align-items:center;gap:8px;margin-top:10px;width:100%;overflow-anchor:none;order:11;';
      picker.appendChild(err);
    }

    if (err) {
      err.innerHTML =
        '<span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background-color:#d93838;color:#fff;font-size:11px;font-weight:bold;flex-shrink:0;">!</span> ' +
        message;
      err.style.display = 'flex';
    }

    this.scrollToSize(missing);
  },

  isAddOrPayTarget(el) {
    if (!el || !el.closest) return false;
    return !!(
      el.closest('[name="add"]') ||
      el.closest('.product-form__submit') ||
      el.closest('.sticky-btn') ||
      el.closest('[id^="StickyAddToCartBtn"]') ||
      el.closest('.shopify-payment-button') ||
      el.closest('.shopify-payment-button__button') ||
      el.closest('[data-shopify="payment-button"]')
    );
  },

  rewriteVariantErrorText() {
    const root = document.querySelector('product-info');
    const missing = this.getMissing(root);
    if (!missing.length) return;
    const message = this.getMessage(missing);
    document.querySelectorAll('.product-form__error-message').forEach((el) => {
      const text = (el.textContent || '').toLowerCase();
      if (text.includes('variant') || text.includes('select')) {
        el.textContent = message;
        el.closest('.product-form__error-message-wrapper')?.setAttribute('hidden', '');
        this.showError(root, missing);
      }
    });
  },

  init() {
    if (this._inited) return;
    this._inited = true;

    const intercept = (event) => {
      const target = event.target;
      if (!target || !target.closest) return;

      const isSubmit =
        event.type === 'submit' &&
        typeof target.matches === 'function' &&
        target.matches('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
      const isAddClick = event.type !== 'submit' && this.isAddOrPayTarget(target);
      if (!isSubmit && !isAddClick) return;

      const root = target.closest?.('product-info') || document.querySelector('product-info');
      const missing = this.getMissing(root);
      if (!missing.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      this.showError(root, missing);
    };

    document.addEventListener('click', intercept, true);
    document.addEventListener('pointerdown', intercept, true);
    document.addEventListener('submit', intercept, true);
  },
};

window.ProductSizeGuard.init();

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    // Mark outgoing picker before insert so connectedCallback can detect an AJAX swap.
    const isVariantPicker =
      oldNode.tagName === 'VARIANT-SELECTS' || oldNode.tagName === 'VARIANT-RADIOS';

    if (isVariantPicker) {
      oldNode.classList.add('js-html-update-outgoing');
      // Hide BEFORE inserting the new picker so mobile never paints two size rows.
      oldNode.style.setProperty('display', 'none', 'important');
      oldNode.setAttribute('hidden', '');
      oldNode.setAttribute('aria-hidden', 'true');
    }

    oldNode.parentNode.insertBefore(newNode, oldNode);

    // Only the variant picker needs the mobile double-row fix.
    // Do NOT change behavior for product-info / main swaps (Add to Bag depends on those).
    if (!isVariantPicker) {
      oldNode.style.display = 'none';
    }

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    if (isVariantPicker) {
      // Remove right away so Add to Bag never reads an unchecked duplicate picker.
      oldNode.remove();
    } else {
      setTimeout(() => oldNode.remove(), 500);
    }
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    // Product accordion manages aria-expanded itself during animated open/close.
    if (event.currentTarget.closest('.product__accordion')) return;
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus({ preventScroll: true });

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  /* Skip lifestyle autoplay loops — product zoom/modal must not leave them paused */
  document.querySelectorAll('video').forEach((video) => {
    if (
      video.classList.contains('lifestyle-video') ||
      video.closest('.lifestyle-image-block') ||
      video.getAttribute('data-keep-playing') === 'true'
    ) {
      return;
    }
    video.pause();
  });
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.onInputBlur = this.onInputBlur.bind(this);
    this.input.addEventListener('blur', this.onInputBlur);
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
    if (this.input) this.input.removeEventListener('blur', this.onInputBlur);
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  /* iOS Safari zooms focused inputs under 16px and often stays zoomed after Done */
  onInputBlur() {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    const original = meta.getAttribute('content') || 'width=device-width, initial-scale=1';
    meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
    setTimeout(() => {
      meta.setAttribute('content', original);
    }, 300);
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.hw-facet-ignore):not(.hw-fs-done):not(.hw-fs-price-card):not(.hw-fs-chip):not(.hw-fs-close):not(.hw-fs-pill)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  isFilterAccordion(el) {
    return !!(
      el &&
      (el.classList.contains('hw-fs-accordion') ||
        el.classList.contains('hw-facet-accordion') ||
        el.classList.contains('hw-fs-accordion__summary') ||
        el.closest?.('.hw-fs-accordion, .hw-facet-accordion'))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    if (this.isFilterAccordion(openDetailsElement) && openDetailsElement !== this.mainDetailsToggle) {
      openDetailsElement.removeAttribute('open');
      return;
    }

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    if (this.isFilterAccordion(detailsElement) || this.isFilterAccordion(summaryElement)) {
      return;
    }
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const target = event.currentTarget;
    if (
      target.classList.contains('hw-facet-ignore') ||
      target.classList.contains('hw-fs-done') ||
      target.classList.contains('hw-fs-price-card') ||
      target.classList.contains('hw-fs-chip') ||
      target.classList.contains('hw-fs-close') ||
      target.classList.contains('hw-fs-pill')
    ) {
      return;
    }
    const detailsElement = target.closest('details');
    if (!detailsElement || this.isFilterAccordion(detailsElement)) return;
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 450) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.hwDrawerGeneration = 0;
    this.hwScrollLocked = false;
    this.hwScrollY = 0;
    this.hwMobileQuery = window.matchMedia('(max-width: 749px)');
    this.hwPanelClosers = new WeakMap();
  }

  isMobileMenuViewport() {
    return this.hwMobileQuery.matches;
  }

  reducedMotionPreferred() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  hwCancelPanelClose(detailsElement) {
    const pending = this.hwPanelClosers.get(detailsElement);
    if (!pending) return;
    clearTimeout(pending.timer);
    pending.panel?.removeEventListener('transitionend', pending.onEnd);
    this.hwPanelClosers.delete(detailsElement);
  }

  lockMobilePageScroll() {
    if (this.hwScrollLocked) return;
    this.hwScrollLocked = true;
    this.hwScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = '';
    document.body.style.position = '';
    document.documentElement.classList.add('hw-mobile-nav-scroll-lock');
    document.body.classList.add('hw-mobile-nav-scroll-lock');

    this.hwOnTouchMove = (event) => {
      if (event.target.closest('.menu-drawer, .menu-drawer__submenu, .menu-drawer__inner-submenu')) return;
      event.preventDefault();
    };
    this.hwOnScrollLock = () => {
      if (!this.hwScrollLocked) return;
      if (Math.abs((window.scrollY || 0) - this.hwScrollY) > 1) {
        window.scrollTo(0, this.hwScrollY);
      }
    };

    document.addEventListener('touchmove', this.hwOnTouchMove, { passive: false });
    window.addEventListener('scroll', this.hwOnScrollLock, { passive: true });
  }

  unlockMobilePageScroll() {
    if (!this.hwScrollLocked) return;
    const scrollY = this.hwScrollY || 0;
    this.hwScrollLocked = false;
    document.removeEventListener('touchmove', this.hwOnTouchMove);
    window.removeEventListener('scroll', this.hwOnScrollLock);
    this.hwOnTouchMove = null;
    this.hwOnScrollLock = null;
    document.documentElement.classList.remove('hw-mobile-nav-scroll-lock');
    document.body.classList.remove('hw-mobile-nav-scroll-lock');
    document.body.style.top = '';
    document.body.style.position = '';
    document.body.style.paddingRight = '';
    window.scrollTo(0, scrollY);
  }

  getMainDrawer() {
    if (!this.hwMainDrawer) {
      this.hwMainDrawer = this.mainDetailsToggle?.querySelector(':scope > .menu-drawer') || null;
    }
    return this.hwMainDrawer;
  }

  /* iOS Safari positions a fixed element against any transformed / sticky ancestor,
     so inside the header the drawer lands off-screen once the page is scrolled.
     While open on mobile the drawer lives directly under <body>. */
  getMenuPortal() {
    if (!this.hwMenuPortal) {
      const portal = document.createElement('div');
      portal.className = 'hw-menu-portal';
      portal.addEventListener('keyup', this.onKeyUp.bind(this));
      this.hwMenuPortal = portal;
    }
    if (!this.hwMenuPortal.isConnected) document.body.appendChild(this.hwMenuPortal);
    return this.hwMenuPortal;
  }

  mountDrawerInPortal() {
    const drawer = this.getMainDrawer();
    if (!drawer) return;
    const portal = this.getMenuPortal();
    if (drawer.parentNode !== portal) portal.appendChild(drawer);
    this.setBrowserThemeColor('#ffffff');
  }

  restoreDrawerFromPortal() {
    this.setBrowserThemeColor(null);
    const drawer = this.getMainDrawer();
    if (!drawer || drawer.parentNode === this.mainDetailsToggle) return;
    this.mainDetailsToggle.appendChild(drawer);
  }

  /* Safari tints its bars from theme-color; force white while the menu is open. */
  setBrowserThemeColor(color) {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (color) {
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        meta.dataset.hwCreated = 'true';
        document.head.appendChild(meta);
      }
      if (this.hwPrevThemeColor === undefined) this.hwPrevThemeColor = meta.getAttribute('content');
      meta.setAttribute('content', color);
      return;
    }
    if (!meta) return;
    if (meta.dataset.hwCreated === 'true') {
      meta.remove();
    } else if (this.hwPrevThemeColor !== undefined && this.hwPrevThemeColor !== null) {
      meta.setAttribute('content', this.hwPrevThemeColor);
    }
    this.hwPrevThemeColor = undefined;
  }

  positionMobileDrawer() {
    const portal = this.hwMenuPortal;
    const bar = this.closest('.header-wrapper') || this.closest('.header');
    if (!portal || !bar) return;

    const rect = bar.getBoundingClientRect();
    let top = Math.round(rect.bottom);
    if (rect.top < -1 || top <= 0 || top > window.innerHeight * 0.5) {
      top = Math.max(48, Math.round(rect.height) || 64);
    }

    portal.style.setProperty('--hw-portal-offset', `${top}px`);
    document.documentElement.style.setProperty('--header-bottom-position', `${top}px`);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }

  resetDrawerScroll() {
    const drawer = this.getMainDrawer();
    if (!drawer) return;
    const nodes = [
      drawer,
      ...drawer.querySelectorAll(
        '.menu-drawer__inner-container, .menu-drawer__navigation-container, .menu-drawer__navigation, .menu-drawer__inner-submenu, .menu-drawer__menu'
      ),
    ];
    nodes.forEach((node) => {
      if (node.scrollTop) node.scrollTop = 0;
      if (node.scrollLeft) node.scrollLeft = 0;
    });
  }

  scheduleDrawerScrollReset() {
    this.hwScrollResetTimers?.forEach((timer) => clearTimeout(timer));
    this.hwScrollResetTimers = [0, 40, 120, 320, 640].map((delay) =>
      setTimeout(() => {
        if (!document.body.classList.contains('hw-mobile-menu-active')) return;
        this.resetDrawerScroll();
      }, delay)
    );
  }

  resetNestedMenus() {
    const roots = [this.mainDetailsToggle, this.getMainDrawer()].filter(Boolean);
    roots.forEach((root) => {
      root.querySelectorAll('details').forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
      root.querySelectorAll('.submenu-open').forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });
    });
  }

  onSummaryClick(event) {
    if (!this.isMobileMenuViewport()) {
      super.onSummaryClick(event);
      return;
    }

    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = this.reducedMotionPreferred();

    const addTrapFocus = () => {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    };

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      return;
    }

    this.hwCancelPanelClose(detailsElement);

    const startSubmenu = () => {
      detailsElement.classList.add('menu-opening');
      summaryElement.setAttribute('aria-expanded', true);
      parentMenuElement && parentMenuElement.classList.add('submenu-open');
      reducedMotion
        ? addTrapFocus()
        : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
    };

    requestAnimationFrame(() => {
      detailsElement.querySelector('.menu-drawer__submenu')?.getBoundingClientRect();
      requestAnimationFrame(startSubmenu);
    });
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    if (!this.isMobileMenuViewport()) {
      setTimeout(() => {
        this.mainDetailsToggle.classList.add('menu-opening');
      });
      return;
    }

    this.hwCancelPanelClose(this.mainDetailsToggle);
    this.hwDrawerGeneration += 1;
    const generation = this.hwDrawerGeneration;
    this.resetNestedMenus();
    document.body.classList.add('hw-mobile-menu-active');
    this.lockMobilePageScroll();
    this.mountDrawerInPortal();
    this.hwMenuPortal.classList.remove('is-open');
    this.hwMenuPortal.classList.add('is-covering');
    document.body.classList.add('hw-menu-page-hidden');
    this.positionMobileDrawer();
    this.resetDrawerScroll();

    const startOpen = () => {
      if (generation !== this.hwDrawerGeneration) return;
      this.positionMobileDrawer();
      this.mainDetailsToggle.classList.add('menu-opening');
      this.hwMenuPortal.classList.add('is-open');
      this.resetDrawerScroll();
      this.scheduleDrawerScrollReset();
    };

    requestAnimationFrame(() => {
      this.getMainDrawer()?.getBoundingClientRect();
      requestAnimationFrame(startOpen);
    });
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!this.isMobileMenuViewport()) {
      if (!elementToFocus) return;
      super.closeMenuDrawer(event, elementToFocus);
      this.header.classList.remove('menu-open');
      window.removeEventListener('resize', this.onResize);
      return;
    }

    if (event === undefined) return;

    this.hwDrawerGeneration += 1;
    const generation = this.hwDrawerGeneration;
    document.body.classList.remove('hw-menu-page-hidden');
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.hwMenuPortal?.classList.remove('is-open', 'is-covering');
    removeTrapFocus(elementToFocus);
    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);

    this.closeAnimation(this.mainDetailsToggle, () => {
      if (generation !== this.hwDrawerGeneration) return;
      this.resetNestedMenus();
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
      this.hwScrollResetTimers?.forEach((timer) => clearTimeout(timer));
      this.hwScrollResetTimers = [];
      this.restoreDrawerFromPortal();
      this.unlockMobilePageScroll();
      document.body.classList.remove('hw-mobile-menu-active');
      this.header.classList.remove('menu-open');
      window.removeEventListener('resize', this.onResize);
    });
  }

  closeAnimation(detailsElement, onComplete) {
    if (!this.isMobileMenuViewport()) {
      super.closeAnimation(detailsElement);
      return;
    }

    const isMain = detailsElement === this.mainDetailsToggle;
    const panel = isMain
      ? this.getMainDrawer()
      : detailsElement.querySelector(':scope > .menu-drawer__submenu') ||
        detailsElement.querySelector('.menu-drawer__submenu');
    const endProperty = 'transform';
    const generation = this.hwDrawerGeneration;
    let completed = false;

    const finish = () => {
      if (completed) return;
      completed = true;
      this.hwCancelPanelClose(detailsElement);
      if (isMain && generation !== this.hwDrawerGeneration) return;
      detailsElement.removeAttribute('open');
      if (detailsElement.closest('details[open]')) {
        trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
      }
      onComplete?.();
    };

    if (!panel || this.reducedMotionPreferred()) {
      finish();
      return;
    }

    const onEnd = (event) => {
      if (event.target !== panel) return;
      if (event.propertyName && event.propertyName !== endProperty) return;
      finish();
    };

    this.hwCancelPanelClose(detailsElement);
    panel.addEventListener('transitionend', onEnd);
    this.hwPanelClosers.set(detailsElement, {
      panel,
      onEnd,
      timer: setTimeout(finish, isMain ? 600 : 560),
    });
  }

  onResize = () => {
    if (document.body.classList.contains('hw-mobile-menu-active')) {
      this.positionMobileDrawer();
      return;
    }

    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      const button = event.target.closest('button, .link');
      if (!button) return;

      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);


class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const isPickerSwap =
      this.nextElementSibling &&
      this.nextElementSibling.classList.contains('js-html-update-outgoing');

    if (!isPickerSwap) {
      (window.ProductSizeGuard || { uncheckSizeRadios(root) {
        root.querySelectorAll('input[type="radio"]').forEach((input) => {
          const name = (input.name || '').toLowerCase();
          if (name.includes('size') || name.includes('top') || name.includes('bottom')) {
            input.checked = false;
            input.removeAttribute('checked');
          }
        });
      } }).uncheckSizeRadios(this);
    }
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      const targetName = (target.name || target.getAttribute('name') || '').toLowerCase();

      if (window.ProductSizeGuard?.isSizeInput(target)) {
        const unavailable =
          target.classList.contains('disabled') ||
          target.classList.contains('visually-disabled') ||
          target.hasAttribute('disabled');
        if (unavailable) {
          // Never allow an out-of-stock size to remain selected.
          target.checked = false;
          target.removeAttribute('checked');
          return;
        }

        const missing = window.ProductSizeGuard.getMissing(this);
        const err = document.getElementById('custom-size-error-msg');
        if (err && !missing.length) err.style.display = 'none';
      }

      // Colour change: clear size immediately (before option values are read for the fetch).
      // Never leave an unavailable size checked, and never auto-select another size.
      const legendTextForColour = (
        target.closest('fieldset, .product-form__input')?.querySelector('legend')?.textContent || ''
      ).toLowerCase();
      const isColourClick =
        targetName.includes('color') ||
        targetName.includes('colour') ||
        legendTextForColour.includes('color') ||
        legendTextForColour.includes('colour');
      if (isColourClick) {
        window.ProductSizeGuard?.uncheckSizeRadios(this);
      }

      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      this.updateSelectionMetadata({ target: checkedInput });
    }
  }
  
  updateSelectionMetadata({ target }) {
    if (!target) return;
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        ?.removeAttribute('selected');
      target.selectedOptions[0]?.setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0]?.dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        ?.querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const targetName = (target.name || '').toLowerCase();
      const fieldset = target.closest('.product-form__input, fieldset');
      const legendText = (fieldset?.querySelector('legend')?.textContent || '').toLowerCase();
      const isColour =
        targetName.includes('color') ||
        targetName.includes('colour') ||
        legendText.includes('color') ||
        legendText.includes('colour');

      // Always update the written colour name next to "Color:"
      if (isColour && value) {
        const colorName =
          fieldset?.querySelector('.selected-color-name') ||
          document.querySelector('.selected-color-name');
        if (colorName) colorName.textContent = value;
      }

      if (!targetName.includes('size')) {
        const selectedSwatchValue = fieldset?.querySelector('[data-selected-value]');
        if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input[type="radio"]:checked'))
      .map(({ dataset }) => dataset.optionValueId)
      .filter(Boolean);
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.querySelectorAll('.card-image-slide img, .card__media img').forEach((img) => {
            img.loading = 'eager';
            if (!img.complete && img.src) {
              img.src = img.src;
            }
          });
          if (typeof window.hwInitCardSliders === 'function') {
            window.hwInitCardSliders(this);
          }
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


/* ==========================================
   Search Product Card Swipe (Mobile Only)
========================================== */

/*document.addEventListener("DOMContentLoaded", () => {
  if (window.innerWidth >= 750) return;

  document.querySelectorAll(".card-image-slider__track").forEach((track) => {
    const slides = track.querySelectorAll(".card-image-slide");
    const counter = track.parentElement.querySelector(".card-image-slider__count");

    if (!slides.length || !counter) return;

    counter.textContent = `1/${slides.length}`;

    track.addEventListener(
      "scroll",
      () => {
        const index = Math.round(track.scrollLeft / track.clientWidth);
        counter.textContent = `${index + 1}/${slides.length}`;
      },
      { passive: true }
    );
  });
});  By om 24-07*/

/* ==========================================
   Dynamic Product Card Swipe & Counter (Mobile)
========================================== */
function initCardImageSliders() {
  if (window.innerWidth >= 750) return;

  document.querySelectorAll(".card-image-slider__track").forEach((track) => {
    if (track.dataset.sliderInitialized) return;
    track.dataset.sliderInitialized = "true";

    const slides = track.querySelectorAll(".card-image-slide");
    const counter = track.parentElement.querySelector(".card-image-slider__count");

    if (!slides.length || !counter) return;

    counter.textContent = `1/${slides.length}`;

    track.addEventListener(
      "scroll",
      () => {
        const index = Math.round(track.scrollLeft / track.clientWidth);
        counter.textContent = `${index + 1}/${slides.length}`;
      },
      { passive: true }
    );
  });
}

// Run on page load
document.addEventListener("DOMContentLoaded", initCardImageSliders);

// Run dynamically whenever search results update via AJAX
const searchObserver = new MutationObserver(() => {
  initCardImageSliders();
});

searchObserver.observe(document.body, {
  childList: true,
  subtree: true
});
/* ==========================================
   Product Card Image Slider (Delegated Handler)
========================================== */
document.addEventListener('click', (event) => {
  const prevBtn = event.target.closest('.card-slider__button--prev');
  const nextBtn = event.target.closest('.card-slider__button--next');

  if (prevBtn || nextBtn) {
    event.preventDefault();
    event.stopPropagation();

    const button = prevBtn || nextBtn;
    const card = button.closest('.card-wrapper, .card, .grid__item');
    const track = card?.querySelector('.card-image-slider__track');

    if (track) {
      const slideWidth = track.clientWidth || 250;
      const scrollAmount = prevBtn ? -slideWidth : slideWidth;
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }
});
document.addEventListener("DOMContentLoaded", () => {
  const DURATION = 400;
  const EASING = "cubic-bezier(0.4, 0, 0.2, 1)";
  const DESKTOP_TRANSITION = `height ${DURATION}ms ${EASING}, opacity ${DURATION}ms ${EASING}, transform ${DURATION}ms ${EASING}`;
  const MOBILE_TRANSITION = `height ${DURATION}ms ${EASING}, opacity 250ms ease`;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const mobileQuery = window.matchMedia("(max-width: 749px)");
  const running = new WeakMap();

  function isMobile() {
    return mobileQuery.matches;
  }

  function getContent(details) {
    return details.querySelector(":scope > .accordion__content");
  }

  function getSummary(details) {
    return details.querySelector(":scope > summary");
  }

  function setExpandedState(details, expanded) {
    const summary = getSummary(details);
    if (summary) summary.setAttribute("aria-expanded", expanded ? "true" : "false");
  }

  function stopRunning(details) {
    const prev = running.get(details);
    if (!prev) return;
    if (prev.timeout) clearTimeout(prev.timeout);
    if (prev.raf) cancelAnimationFrame(prev.raf);
    if (prev.content && prev.onEnd) {
      prev.content.removeEventListener("transitionend", prev.onEnd);
    }
    running.delete(details);
  }

  function currentHeight(content) {
    return content.getBoundingClientRect().height;
  }

  function measureFullHeight(content) {
    const prevHeight = content.style.height;
    const prevOpacity = content.style.opacity;
    const prevTransform = content.style.transform;
    const prevTransition = content.style.transition;
    content.style.transition = "none";
    content.style.height = "auto";
    content.style.opacity = "1";
    if (!isMobile()) content.style.transform = "translateY(0)";
    const full = content.scrollHeight;
    content.style.height = prevHeight;
    content.style.opacity = prevOpacity;
    content.style.transform = prevTransform;
    content.style.transition = prevTransition;
    void content.offsetHeight;
    return full;
  }

  function setClosedVisual(content) {
    content.style.height = "0px";
    content.style.opacity = "0";
    content.style.overflow = "hidden";
    content.style.transform = isMobile() ? "none" : "translateY(-4px)";
  }

  function setOpenVisual(content) {
    content.style.height = "auto";
    content.style.opacity = "1";
    content.style.transform = isMobile() ? "none" : "translateY(0)";
    content.style.overflow = "hidden";
  }

  function lockPixelHeight(content) {
    const px = Math.ceil(Math.max(currentHeight(content), content.scrollHeight, 0));
    content.style.transition = "none";
    content.style.overflow = "hidden";
    content.style.height = px + "px";
    content.style.opacity = getComputedStyle(content).opacity || "1";
    if (isMobile()) content.style.transform = "none";
    void content.offsetHeight;
    return px;
  }

  function animatePanel(details, content, fromHeight, toHeight, opening, onDone) {
    stopRunning(details);

    const mobile = isMobile();
    content.style.overflow = "hidden";
    content.style.transition = "none";
    content.style.height = Math.max(0, fromHeight) + "px";

    if (mobile) {
      content.style.transform = "none";
      content.style.opacity = opening && fromHeight < 1 ? "0" : getComputedStyle(content).opacity || (opening ? "0" : "1");
    } else {
      content.style.opacity = opening && fromHeight < 1 ? "0" : opening ? "1" : getComputedStyle(content).opacity || "1";
      content.style.transform =
        opening && fromHeight < 1 ? "translateY(-4px)" : opening ? "translateY(0)" : getComputedStyle(content).transform || "translateY(0)";
    }
    void content.offsetHeight;

    if (reduceMotion || Math.abs(fromHeight - toHeight) < 0.5) {
      if (toHeight <= 0) setClosedVisual(content);
      else setOpenVisual(content);
      content.style.transition = "";
      onDone();
      return;
    }

    let settled = false;
    const finish = (event) => {
      if (event && event.target !== content) return;
      if (event && event.propertyName && event.propertyName !== "height") return;
      if (settled) return;
      settled = true;
      stopRunning(details);

      if (toHeight <= 0) {
        content.style.transition = "none";
        content.style.height = "0px";
        content.style.opacity = "0";
        content.style.transform = mobile ? "none" : "translateY(-4px)";
        content.style.overflow = "hidden";
        void content.offsetHeight;
      } else {
        setOpenVisual(content);
        content.style.transition = "";
      }
      onDone();
    };

    const record = { content, onEnd: finish, timeout: 0, raf: 0 };
    running.set(details, record);
    content.addEventListener("transitionend", finish);

    record.raf = requestAnimationFrame(() => {
      record.raf = requestAnimationFrame(() => {
        content.style.transition = mobile ? MOBILE_TRANSITION : DESKTOP_TRANSITION;
        content.style.height = Math.max(0, toHeight) + "px";
        content.style.opacity = opening ? "1" : "0";
        if (!mobile) {
          content.style.transform = opening ? "translateY(0)" : "translateY(-4px)";
        }
        record.timeout = setTimeout(finish, DURATION + 100);
      });
    });
  }

  function openAccordion(details) {
    const content = getContent(details);
    if (!content) {
      details.setAttribute("open", "");
      details.classList.add("is-expanded");
      details.classList.remove("is-closing");
      setExpandedState(details, true);
      return;
    }

    details.classList.remove("is-closing");
    const fromHeight = details.hasAttribute("open") ? currentHeight(content) : 0;

    content.style.overflow = "hidden";
    content.style.transition = "none";
    content.style.height = `${fromHeight}px`;
    details.setAttribute("open", "");
    details.classList.add("is-expanded");
    setExpandedState(details, true);

    const toHeight = measureFullHeight(content);
    animatePanel(details, content, fromHeight, toHeight, true, () => {});
  }

  function closeAccordion(details) {
    if (!details.hasAttribute("open") && !details.classList.contains("is-expanded")) return;

    const content = getContent(details);
    if (!content) {
      details.removeAttribute("open");
      details.classList.remove("is-expanded", "is-closing");
      setExpandedState(details, false);
      return;
    }

    const mobile = isMobile();
    const fromHeight = lockPixelHeight(content);
    details.classList.add("is-closing");
    details.classList.remove("is-expanded");
    setExpandedState(details, false);

    // Keep `open` until height reaches 0 so the browser does not snap-hide content.
    animatePanel(details, content, fromHeight, 0, false, () => {
      // One more frame at height:0 before removing open — kills the mobile end-snap.
      requestAnimationFrame(() => {
        content.style.transition = "none";
        content.style.height = "0px";
        content.style.opacity = "0";
        content.style.transform = mobile ? "none" : "translateY(-4px)";
        content.style.overflow = "hidden";
        void content.offsetHeight;

        details.removeAttribute("open");
        details.classList.remove("is-closing");
        setClosedVisual(content);
        content.style.transition = "";
      });
    });
  }

  document.querySelectorAll(".product__accordion details").forEach((details) => {
    const content = getContent(details);
    if (!content) return;
    details.classList.remove("is-expanded", "is-closing");
    details.removeAttribute("open");
    setClosedVisual(content);
    content.style.transition = "";
    setExpandedState(details, false);
  });

  document.addEventListener("click", (event) => {
    const summary = event.target.closest(".product__accordion details > summary");
    if (!summary) return;

    const accordion = summary.closest("details");
    if (!accordion || !accordion.closest(".product__accordion")) return;

    event.preventDefault();

    document.querySelectorAll(".product__accordion details").forEach((item) => {
      if (item !== accordion) closeAccordion(item);
    });

    if (accordion.classList.contains("is-expanded") && !accordion.classList.contains("is-closing")) {
      closeAccordion(accordion);
    } else {
      openAccordion(accordion);
    }
  });
});